/*import React from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, cleanupAuthState } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";
import { verifyTurnstileToken, checkRateLimit, recordAttempt } from '@/utils/turnstileVerification';
import { fetchUserRoles, isAdminUser, isSuperAdminUser } from './auth-utils';
import { validateEmailForRegistration } from '@/utils/emailValidation';
import { logFailedLogin } from '@/utils/securityMonitoring';

export const useAuthActions = (updateUserRoles: (userId: string) => Promise<{ isAdmin: boolean; isSuperAdmin: boolean; }>) => {
  const { toast } = useToast();

  // Helper function to add timeout to promises
  const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number, fallbackValue: T): Promise<T> => {
    return new Promise<T>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        console.warn(`⏱️ Operation timed out after ${timeoutMs}ms, using fallback`);
        resolve(fallbackValue);
      }, timeoutMs);

      promise
        .then((value) => {
          clearTimeout(timeoutId);
          resolve(value);
        })
        .catch((err) => {
          clearTimeout(timeoutId);
          reject(err);
        });
    });
  };
  // Sign in with email and password
  const signIn = async (email: string, password: string) => {
    try {
      console.log('🔐 [useAuthActions] signIn started for:', email);

      const timestamps = {
        start: Date.now(),
        cleanupComplete: 0,
        signOutComplete: 0,
        signInComplete: 0,
        rolesStart: 0,
        rolesComplete: 0,
        end: 0
      };


      // Clean up existing auth state
      console.log('🔐 [useAuthActions] Cleaning up auth state...');
      cleanupAuthState();

      timestamps.cleanupComplete = Date.now();
      console.log('🔐 [Time] Cleanup:', timestamps.cleanupComplete - timestamps.start, 'ms');

      // Attempt global sign out
      try {
        console.log('🔐 [useAuthActions] Attempting global sign out...');
        await withTimeout(supabase.auth.signOut({ scope: 'global' }), 2000, { error: null } as any);
        console.log('🔐 [useAuthActions] Global sign out complete (or timed out)');
      } catch (err) {
        console.log('🔐 [useAuthActions] Global sign out failed (continuing):', err);
      }

      timestamps.signOutComplete = Date.now();
      console.log('🔐 [Time] SignOut:', timestamps.signOutComplete - timestamps.cleanupComplete, 'ms');

      // Sign in
      console.log('🔐 [useAuthActions] Calling signInWithPassword...');
      const { data, error } = await withTimeout(
        supabase.auth.signInWithPassword({ email, password }),
        10000,
        { data: { user: null, session: null }, error: new Error('Login timed out') } as any
      );

      timestamps.signInComplete = Date.now();
      console.log('🔐 [Time] SignIn:', timestamps.signInComplete - timestamps.signOutComplete, 'ms');

      console.log('🔐 [useAuthActions] signInWithPassword result:', {
        hasUser: !!data?.user,
        hasSession: !!data?.session,
        error: !!error
      });

      if (error) {
        console.error('🔐 [useAuthActions] Sign in error:', error);
        // Log failed login attempt for security monitoring
        logFailedLogin(email, error.message);
        toast({
          title: "Login Failed",
          description: error.message,
          variant: "destructive",
        });
        return { error, data: { user: null, session: null } };
      }

      if (data.user) {
        console.log('🔐 [useAuthActions] User signed in successfully:', data.user.email);

        try {
          console.log('🔐 [useAuthActions] Fetching user roles...');
          timestamps.rolesStart = Date.now();
          console.log('🔐 [Time] About to fetch roles (deferred)...');
          // Defer role loading so login UI never blocks
          setTimeout(() => {
            updateUserRoles(data.user!.id)
              .then(({ isAdmin, isSuperAdmin }) => {
                timestamps.rolesComplete = Date.now();
                console.log('🔐 [Time] Roles loaded:', timestamps.rolesComplete - timestamps.rolesStart, 'ms');
                console.log('🔐 [useAuthActions] User roles loaded:', { isAdmin, isSuperAdmin });
              })
              .catch((roleError) => {
                console.error('🔐 [useAuthActions] Role fetch failed (deferred):', roleError);
              });
          }, 0);
        } catch (roleError) {
          console.error('🔐 [useAuthActions] Role fetch scheduling failed:', roleError);
          // Continue regardless
        }

        toast({
          title: "Login Successful",
          description: "You have successfully logged in",
        });
      }

      console.log('🔐 [useAuthActions] signIn completed successfully');
      timestamps.end = Date.now();
      console.log('🔐 [Time] TOTAL DURATION:', timestamps.end - timestamps.start, 'ms');
      console.log('🔐 [Time] Breakdown:', timestamps);
      return { error: null, data };

    } catch (error) {
      console.error('🔐 [useAuthActions] Unexpected error in signIn:', error);
      toast({
        title: "Login Error",
        description: (error as Error).message,
        variant: "destructive",
      });
      return { error: error as Error, data: { user: null, session: null } };
    }
  };

  // Debug function to verify authentication state
  const debugAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    console.log('=== AUTH DEBUG ===');
    console.log('User ID:', session?.user?.id);
    console.log('Email:', session?.user?.email);
    console.log('Session expires at:', session?.expires_at);
    console.log('Auth state from storage:', {
      localStorage: Object.keys(localStorage).filter(key =>
        key.includes('supabase') || key.includes('auth')
      ),
      sessionStorage: typeof sessionStorage !== 'undefined' ?
        Object.keys(sessionStorage).filter(key =>
          key.includes('supabase') || key.includes('auth')
        ) : []
    });
    console.log('==================');
    return {
      userId: session?.user?.id,
      email: session?.user?.email,
      expiresAt: session?.expires_at
    };
  };

  // Sign up with email and password
  const signUp = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    turnstileToken?: string
  ) => {
    try {
      // Check rate limiting
      const rateCheck = checkRateLimit();
      if (!rateCheck.allowed) {
        throw new Error(`Too many attempts. Please wait ${rateCheck.remainingTime} seconds.`);
      }

      // Validate email format and check if it already exists
      console.log('📧 Validating email for registration:', email);
      const emailValidation = await validateEmailForRegistration(email);
      if (!emailValidation.isValid) {
        recordAttempt();
        throw new Error(emailValidation.error);
      }

      // Verify Turnstile token if provided
      if (turnstileToken) {
        console.log('🔐 Verifying Turnstile token...');
        const verification = await verifyTurnstileToken(turnstileToken);
        if (!verification.success) {
          recordAttempt();
          throw new Error(verification.error || 'Security verification failed');
        }
      }

      // Clean up existing auth state
      cleanupAuthState();

      // Sign up with email confirmation required
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) {
        toast({
          title: "Registration Failed",
          description: error.message,
          variant: "destructive",
        });
        return { error, data: { user: null, session: null } };
      }

      toast({
        title: "Registration Successful",
        description: "Please check your email and click the confirmation link before logging in.",
      });

      return { error: null, data };
    } catch (error) {
      toast({
        title: "Registration Error",
        description: (error as Error).message,
        variant: "destructive",
      });
      return { error: error as Error, data: { user: null, session: null } };
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      console.log('=== LOGOUT DEBUG START ===');

      // Sign out from Supabase FIRST
      await withTimeout(supabase.auth.signOut({ scope: 'global' }), 2000, { error: null } as any);
      console.log('✅ Supabase signOut completed (or timed out)');

      // Clean up auth state
      cleanupAuthState();
      console.log('✅ Auth state cleaned');

      // Clear ALL user-specific localStorage data
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.includes('cart') ||
          key.includes('user') ||
          key.includes('profile') ||
          key.includes('checkout') ||
          key.includes('favorites') ||
          key.includes('client') ||
          key.includes('delivery')
        )) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => {
        console.log(`🗑️ Removing localStorage key: ${key}`);
        localStorage.removeItem(key);
      });

      // Also clear sessionStorage
      const sessionKeysToRemove = [];
      if (typeof sessionStorage !== 'undefined') {
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && (
            key.includes('cart') ||
            key.includes('user') ||
            key.includes('profile') ||
            key.includes('checkout')
          )) {
            sessionKeysToRemove.push(key);
          }
        }

        sessionKeysToRemove.forEach(key => {
          console.log(`🗑️ Removing sessionStorage key: ${key}`);
          sessionStorage.removeItem(key);
        });
      }

      console.log('✅ All user data cleared from storage');
      console.log('=== LOGOUT DEBUG END ===');

      toast({
        title: "Logged Out",
        description: "You have been logged out successfully",
      });

      // Redirect to login page
      window.location.href = '/login';
    } catch (error) {
      console.error('Sign out error:', error);
      toast({
        title: "Logout Error",
        description: (error as Error).message,
        variant: "destructive",
      });
    }
  };

  return {
    signIn,
    signUp,
    signOut,
    debugAuth
  };
};
*/
import React from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, cleanupAuthState } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";
import { verifyTurnstileToken, checkRateLimit, recordAttempt } from '@/utils/turnstileVerification';
import { fetchUserRoles, isAdminUser, isSuperAdminUser } from './auth-utils';
import { validateEmailForRegistration } from '@/utils/emailValidation';
import { logFailedLogin } from '@/utils/securityMonitoring';

/**
 * useAuthActions Hook
 * Provides all core Supabase authentication actions.
 * Fixed version (Oct 2025)
 */
export const useAuthActions = (
  updateUserRoles: (userId: string) => Promise<{ isAdmin: boolean; isSuperAdmin: boolean; }>
) => {
  const { toast } = useToast();

  // Helper to timeout async operations
  const withTimeout = <T,>(
    promise: Promise<T>,
    timeoutMs: number,
    errorMessage: string
  ): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error(errorMessage)), timeoutMs)
      ),
    ]);
  };

  /**
   * LOGIN
   * Handles email/password sign-in and deferred role loading
   */
  const signIn = async (email: string, password: string) => {
    try {
      console.log('🔐 [useAuthActions] signIn started for:', email);
      const startTime = Date.now();

      // ✅ Do NOT cleanup or signOut here — it breaks Supabase auth events

      // Attempt sign-in with timeout
      console.log('🔐 [useAuthActions] Calling signInWithPassword...');
      const { data, error } = await withTimeout(
        supabase.auth.signInWithPassword({ email, password }),
        30000,
        'Login request timed out after 30 seconds. Please check your internet connection.'
      );

      const duration = Date.now() - startTime;
      console.log('🔐 [useAuthActions] signInWithPassword completed in:', duration, 'ms');

      if (error) {
        console.error('🔐 [useAuthActions] Sign in error:', error);
        logFailedLogin(email, error.message);
        toast({
          title: "Login Failed",
          description: error.message || "Incorrect email or password",
          variant: "destructive",
        });
        return { error, data: { user: null, session: null } };
      }

      if (data?.user) {
        console.log('✅ [useAuthActions] User signed in successfully:', data.user.email);

        // Debug session confirmation
        const { data: sessionCheck } = await supabase.auth.getSession();
        console.log('🧩 [useAuthActions] Session after login:', sessionCheck?.session?.user?.email || 'No active session');

        // Defer role loading (non-blocking)
        setTimeout(() => {
          console.log('🔐 [useAuthActions] Loading user roles (deferred)...');
          updateUserRoles(data.user!.id)
            .then(({ isAdmin, isSuperAdmin }) => {
              console.log('🔐 [useAuthActions] Roles loaded:', { isAdmin, isSuperAdmin });
            })
            .catch((err) => console.error('🔐 [useAuthActions] Role fetch failed:', err));
        }, 0);

        toast({
          title: "Login Successful",
          description: "You are now signed in.",
        });
      }

      console.log('✅ [useAuthActions] signIn completed successfully');
      return { error: null, data };
    } catch (error: any) {
      console.error('🔐 [useAuthActions] Unexpected error in signIn:', error);
      toast({
        title: "Login Error",
        description: error.message || "Unexpected authentication error",
        variant: "destructive",
      });
      return { error, data: { user: null, session: null } };
    }
  };

  /**
   * SIGN-UP
   * Registers a new user with Turnstile verification and validation
   */
  const signUp = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    turnstileToken?: string
  ) => {
    try {
      const rateCheck = checkRateLimit();
      if (!rateCheck.allowed) {
        throw new Error(`Too many attempts. Please wait ${rateCheck.remainingTime} seconds.`);
      }

      console.log('📧 Validating email for registration:', email);
      const emailValidation = await validateEmailForRegistration(email);
      if (!emailValidation.isValid) {
        recordAttempt();
        throw new Error(emailValidation.error);
      }

      if (turnstileToken) {
        console.log('🔐 Verifying Turnstile token...');
        const verification = await verifyTurnstileToken(turnstileToken);
        if (!verification.success) {
          recordAttempt();
          throw new Error(verification.error || 'Security verification failed');
        }
      }

      // Safe cleanup before sign-up (not before login)
      cleanupAuthState();

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { first_name: firstName, last_name: lastName },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        toast({
          title: "Registration Failed",
          description: error.message,
          variant: "destructive",
        });
        return { error, data: { user: null, session: null } };
      }

      toast({
        title: "Registration Successful",
        description: "Check your email to confirm your account before logging in.",
      });

      return { error: null, data };
    } catch (error: any) {
      toast({
        title: "Registration Error",
        description: error.message,
        variant: "destructive",
      });
      return { error, data: { user: null, session: null } };
    }
  };

  /**
   * SIGN-OUT
   * Safely signs out globally and clears session data
   */
  const signOut = async () => {
    try {
      console.log('🚪 [useAuthActions] Signing out...');
      await withTimeout(
        supabase.auth.signOut({ scope: 'global' }),
        5000,
        'Sign out timeout'
      ).catch((err) => console.warn('Sign out timed out:', err));

      cleanupAuthState();
      console.log('✅ Auth state cleaned');

      toast({
        title: "Logged Out",
        description: "You have been logged out successfully",
      });

      window.location.href = '/login';
    } catch (error: any) {
      console.error('Sign out error:', error);
      toast({
        title: "Logout Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  /**
   * DEBUG
   * Prints the current Supabase auth session
   */
  const debugAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    console.log('=== AUTH DEBUG ===');
    console.log('User ID:', session?.user?.id);
    console.log('Email:', session?.user?.email);
    console.log('Session expires at:', session?.expires_at);
    console.log('Auth storage keys:', Object.keys(localStorage).filter(k => k.includes('sb-') || k.includes('supabase')));
    console.log('==================');
    return {
      userId: session?.user?.id,
      email: session?.user?.email,
      expiresAt: session?.expires_at
    };
  };

  return {
    signIn,
    signUp,
    signOut,
    debugAuth,
  };
};
